import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "../lib/api";
import { toast } from "sonner";
import { useAuth } from "../context/AuthContext";
import {
  ArrowLeft, MagnifyingGlass, CaretRight, Star, FilmSlate, ClockCounterClockwise, Sparkle, X,
} from "@phosphor-icons/react";

// User-facing official library browser: CATEGORY → SUBCATEGORY → CREATIVE DIRECTOR.
// Only public fields ever reach this page — protected prompts stay server-side.
// Accessible to logged-out guests (per public guest funnel spec): Recent /
// Favorites strips and the star-toggle are hidden for guests because those
// are per-user data. Guests can still browse, search, and select a director;
// clicking "Use this Director" navigates to /ai-pro-studio where the
// approved guest-render flow takes over.

const useDebounced = (value, ms) => {
  const [v, setV] = useState(value);
  useEffect(() => { const t = setTimeout(() => setV(value), ms); return () => clearTimeout(t); }, [value, ms]);
  return v;
};

const DirectorCard = ({ d, onUse, onToggleFav, crumbs, showFav = true }) => (
  <div data-testid={`library-director-${d.slug}`} className="rounded-xl border border-white/10 bg-white/[0.03] hover:border-amber-400/40 transition-colors p-4 flex flex-col">
    <div className="flex items-start gap-2">
      <FilmSlate size={18} weight="fill" className="text-amber-300 mt-0.5 shrink-0" />
      <div className="min-w-0 flex-1">
        <p className="font-bold text-[14px] leading-tight text-white truncate">{d.name}</p>
        {crumbs && <p className="text-[10px] text-zinc-500 mt-0.5 truncate">{crumbs}</p>}
      </div>
      {showFav && (
        <button
          data-testid={`library-director-fav-${d.slug}`}
          onClick={() => onToggleFav(d)}
          title={d.is_favorite ? "Remove from favorites" : "Add to favorites"}
          className="shrink-0 h-7 w-7 rounded-full hover:bg-white/10 flex items-center justify-center transition-colors"
        >
          <Star size={15} weight={d.is_favorite ? "fill" : "regular"} className={d.is_favorite ? "text-amber-300" : "text-zinc-500"} />
        </button>
      )}
    </div>
    {d.short_description && <p className="text-[11.5px] text-zinc-400 leading-snug mt-2 line-clamp-3">{d.short_description}</p>}
    <button
      data-testid={`library-director-use-${d.slug}`}
      onClick={() => onUse(d)}
      className="mt-3 inline-flex items-center justify-center gap-1.5 h-9 rounded-full bg-amber-400 hover:bg-amber-300 text-black text-xs font-bold transition-colors"
    >
      <Sparkle size={14} weight="fill" /> Use this Director
    </button>
  </div>
);

export default function DesignLibrary() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const isGuest = !user;
  const [tree, setTree] = useState([]);
  const [loading, setLoading] = useState(true);
  const [category, setCategory] = useState(null);
  const [subcategory, setSubcategory] = useState(null);
  const [directors, setDirectors] = useState([]);
  const [dirsLoading, setDirsLoading] = useState(false);
  const [recent, setRecent] = useState([]);
  const [favorites, setFavorites] = useState([]);
  const [query, setQuery] = useState("");
  const debounced = useDebounced(query, 300);
  const [results, setResults] = useState(null);

  useEffect(() => {
    // Guests: only fetch public browse. Recent + favorites are per-user data
    // that isn't available to unauthenticated visitors.
    const fetches = [api.get("/library-pro/browse")];
    if (!isGuest) {
      fetches.push(api.get("/library-pro/recent"));
      fetches.push(api.get("/library-pro/favorites"));
    }
    Promise.all(fetches).then((responses) => {
      setTree(responses[0].data.categories || []);
      if (!isGuest) {
        setRecent(responses[1].data || []);
        setFavorites(responses[2].data || []);
      }
    }).catch(() => toast.error("Couldn't load the Design Library."))
      .finally(() => setLoading(false));
  }, [isGuest]);

  useEffect(() => {
    if (debounced.trim().length < 2) { setResults(null); return; }
    api.get(`/library-pro/search?q=${encodeURIComponent(debounced.trim())}`)
      .then((r) => setResults(r.data))
      .catch(() => {});
  }, [debounced]);

  const favIds = useMemo(() => new Set(favorites.map((f) => f.id)), [favorites]);

  const openSubcategory = (cat, sub) => {
    setCategory(cat);
    setSubcategory(sub);
    setDirsLoading(true);
    api.get(`/library-pro/subcategories/${sub.id}/directors`)
      .then((r) => setDirectors(r.data.directors || []))
      .catch(() => toast.error("Couldn't load directors."))
      .finally(() => setDirsLoading(false));
  };

  const applyDirector = (d, cat, sub) => {
    // Recent tracking is per-user; guests skip it and go straight to Studio.
    if (!isGuest) {
      api.post(`/library-pro/recent/${d.id}`).catch(() => {});
    }
    navigate("/ai-pro-studio", {
      state: {
        libraryDirector: {
          id: d.id, name: d.name, slug: d.slug, short_description: d.short_description,
          category_id: cat?.id || d.breadcrumbs?.[0]?.category_id || null,
          subcategory_id: sub?.id || d.breadcrumbs?.[0]?.subcategory_id || null,
          category_name: cat?.name || d.breadcrumbs?.[0]?.category_name || "",
          subcategory_name: sub?.name || d.breadcrumbs?.[0]?.subcategory_name || "",
        },
      },
    });
  };

  const toggleFav = async (d) => {
    // Favorites belong to a user account. Guests can't favorite.
    if (isGuest) return;
    const isFav = d.is_favorite ?? favIds.has(d.id);
    try {
      if (isFav) await api.delete(`/library-pro/favorites/${d.id}`);
      else await api.post(`/library-pro/favorites/${d.id}`);
      const f = await api.get("/library-pro/favorites");
      setFavorites(f.data || []);
      setDirectors((prev) => prev.map((x) => (x.id === d.id ? { ...x, is_favorite: !isFav } : x)));
    } catch (_) {
      toast.error("Couldn't update favorites.");
    }
  };

  const withFav = (list) => list.map((d) => ({ ...d, is_favorite: d.is_favorite ?? favIds.has(d.id) }));

  return (
    <div data-testid="design-library" className="min-h-screen bg-[#070707] text-white font-body">
      <header className="sticky top-0 z-40 backdrop-blur-xl bg-black/60 border-b border-white/10">
        <div className="max-w-[1300px] mx-auto px-6 h-16 flex items-center gap-3">
          <button onClick={() => navigate(isGuest ? "/ai-pro-studio" : "/dashboard")} data-testid="library-back-btn" className="h-9 w-9 rounded-lg hover:bg-white/10 flex items-center justify-center transition-colors"><ArrowLeft size={18} /></button>
          <FilmSlate size={20} weight="fill" className="text-amber-300" />
          <span className="font-display font-extrabold text-lg tracking-tight">Design Library</span>
          <div className="ml-auto relative w-full max-w-xs">
            <MagnifyingGlass size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-500" />
            <input
              data-testid="library-search-input"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search categories, styles, directors…"
              className="w-full h-9 rounded-full bg-white/5 border border-white/10 pl-9 pr-8 text-sm outline-none focus:border-amber-400/50"
            />
            {query && (
              <button onClick={() => setQuery("")} className="absolute right-2.5 top-1/2 -translate-y-1/2 text-zinc-500 hover:text-white"><X size={13} weight="bold" /></button>
            )}
          </div>
        </div>
      </header>

      <main className="max-w-[1300px] mx-auto px-6 py-8">
        {/* Breadcrumbs */}
        <nav data-testid="library-breadcrumbs" className="flex items-center gap-1.5 text-sm mb-6 flex-wrap">
          <button onClick={() => { setCategory(null); setSubcategory(null); }} className={`hover:text-amber-300 transition-colors ${!category ? "text-white font-bold" : "text-zinc-400"}`}>All Categories</button>
          {category && (
            <>
              <CaretRight size={12} className="text-zinc-600" />
              <button onClick={() => setSubcategory(null)} className={`hover:text-amber-300 transition-colors ${!subcategory ? "text-white font-bold" : "text-zinc-400"}`}>{category.icon} {category.name}</button>
            </>
          )}
          {subcategory && (
            <>
              <CaretRight size={12} className="text-zinc-600" />
              <span className="text-white font-bold">{subcategory.name}</span>
            </>
          )}
        </nav>

        {/* Search results override the browse view */}
        {results ? (
          <div data-testid="library-search-results" className="space-y-8">
            {results.categories.length > 0 && (
              <section>
                <p className="text-xs uppercase tracking-[0.16em] text-zinc-500 font-bold mb-3">Categories</p>
                <div className="flex flex-wrap gap-2">
                  {results.categories.map((c) => (
                    <button key={c.id} onClick={() => { setQuery(""); const cat = tree.find((t) => t.id === c.id); if (cat) { setCategory(cat); setSubcategory(null); } }}
                      className="px-4 py-2 rounded-full border border-white/10 bg-white/[0.04] hover:border-amber-400/40 text-sm transition-colors">
                      {c.icon} {c.name}
                    </button>
                  ))}
                </div>
              </section>
            )}
            {results.subcategories.length > 0 && (
              <section>
                <p className="text-xs uppercase tracking-[0.16em] text-zinc-500 font-bold mb-3">Subcategories</p>
                <div className="flex flex-wrap gap-2">
                  {results.subcategories.map((s) => (
                    <button key={s.id} onClick={() => { setQuery(""); const cat = tree.find((t) => t.id === s.category_id); const sub = cat?.subcategories?.find((x) => x.id === s.id); if (cat && sub) openSubcategory(cat, sub); }}
                      className="px-4 py-2 rounded-full border border-white/10 bg-white/[0.04] hover:border-amber-400/40 text-sm transition-colors">
                      {s.name} <span className="text-zinc-500 text-xs">· {s.category_name}</span>
                    </button>
                  ))}
                </div>
              </section>
            )}
            {results.directors.length > 0 && (
              <section>
                <p className="text-xs uppercase tracking-[0.16em] text-zinc-500 font-bold mb-3">Creative Directors</p>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3">
                  {withFav(results.directors).map((d) => (
                    <DirectorCard key={d.id} d={d} onUse={(x) => applyDirector(x)} onToggleFav={toggleFav} showFav={!isGuest}
                      crumbs={d.breadcrumbs?.map((b) => `${b.category_name} → ${b.subcategory_name}`).join(" · ")} />
                  ))}
                </div>
              </section>
            )}
            {results.categories.length === 0 && results.subcategories.length === 0 && results.directors.length === 0 && (
              <p data-testid="library-search-empty" className="text-sm text-zinc-500">No matches for “{debounced}”. Try a different keyword.</p>
            )}
          </div>
        ) : loading ? (
          <div className="flex items-center justify-center py-24 text-zinc-500 text-sm">Loading the library…</div>
        ) : subcategory ? (
          /* LEVEL 3 — Directors */
          <div data-testid="library-directors-view">
            <h1 className="font-display text-2xl font-black tracking-tight mb-1">{subcategory.name}</h1>
            <p className="text-sm text-zinc-400 mb-6">{subcategory.description}</p>
            {dirsLoading ? (
              <p className="text-sm text-zinc-500 py-12 text-center">Loading directors…</p>
            ) : directors.length === 0 ? (
              <p className="text-sm text-zinc-500 py-12 text-center">No Creative Directors published here yet.</p>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3">
                {directors.map((d) => (
                  <DirectorCard key={d.id} d={d} onUse={(x) => applyDirector(x, category, subcategory)} onToggleFav={toggleFav} showFav={!isGuest} />
                ))}
              </div>
            )}
          </div>
        ) : category ? (
          /* LEVEL 2 — Subcategories */
          <div data-testid="library-subcategories-view">
            <h1 className="font-display text-2xl font-black tracking-tight mb-1">{category.icon} {category.name}</h1>
            <p className="text-sm text-zinc-400 mb-6">{category.description}</p>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
              {category.subcategories.map((s) => (
                <button key={s.id} data-testid={`library-sub-${s.id}`} onClick={() => openSubcategory(category, s)}
                  className="text-left rounded-xl border border-white/10 bg-white/[0.03] hover:border-amber-400/40 hover:bg-white/[0.06] transition-colors p-5">
                  <div className="flex items-center justify-between gap-2">
                    <p className="font-bold text-[15px]">{s.name}</p>
                    <CaretRight size={14} className="text-zinc-600 shrink-0" />
                  </div>
                  <p className="text-[12px] text-zinc-500 leading-snug mt-1.5 line-clamp-2">{s.description}</p>
                  <span className="inline-block mt-3 text-[10px] font-semibold px-2 py-0.5 rounded-full bg-amber-400/10 text-amber-300">
                    {s.director_count} director{s.director_count === 1 ? "" : "s"}
                  </span>
                </button>
              ))}
            </div>
          </div>
        ) : (
          /* LEVEL 1 — Categories + recents + favorites */
          <div data-testid="library-categories-view" className="space-y-10">
            {recent.length > 0 && (
              <section data-testid="library-recent-row">
                <p className="text-xs uppercase tracking-[0.16em] text-zinc-500 font-bold mb-3 flex items-center gap-1.5"><ClockCounterClockwise size={13} weight="bold" /> Recently used</p>
                <div className="flex gap-2 overflow-x-auto pb-2">
                  {withFav(recent).map((d) => (
                    <button key={d.id} onClick={() => applyDirector(d)} className="shrink-0 px-4 py-2 rounded-full border border-white/10 bg-white/[0.04] hover:border-amber-400/40 text-sm transition-colors">
                      🎬 {d.name}
                    </button>
                  ))}
                </div>
              </section>
            )}
            {favorites.length > 0 && (
              <section data-testid="library-favorites-row">
                <p className="text-xs uppercase tracking-[0.16em] text-zinc-500 font-bold mb-3 flex items-center gap-1.5"><Star size={13} weight="fill" className="text-amber-300" /> Your favorites</p>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3">
                  {favorites.map((d) => (
                    <DirectorCard key={d.id} d={{ ...d, is_favorite: true }} onUse={(x) => applyDirector(x)} onToggleFav={toggleFav} showFav={!isGuest} />
                  ))}
                </div>
              </section>
            )}
            <section>
              <h1 className="font-display text-3xl font-black tracking-tight mb-1">Browse by category</h1>
              <p className="text-sm text-zinc-400 mb-6">Pick a category, choose a style direction, and let a Creative Director art-direct your next design.</p>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                {tree.map((c) => (
                  <button key={c.id} data-testid={`library-cat-${c.id}`} onClick={() => setCategory(c)}
                    className="text-left rounded-2xl border border-white/10 bg-white/[0.03] hover:border-amber-400/40 hover:bg-white/[0.06] transition-colors p-5">
                    <div className="flex items-center justify-between">
                      <span className="text-2xl">{c.icon}</span>
                      <CaretRight size={14} className="text-zinc-600" />
                    </div>
                    <p className="font-display font-bold text-[16px] mt-3 leading-tight">{c.name}</p>
                    <p className="text-[12px] text-zinc-500 leading-snug mt-1.5 line-clamp-2">{c.description}</p>
                    <span className="inline-block mt-3 text-[10px] font-semibold px-2 py-0.5 rounded-full bg-white/5 text-zinc-400">
                      {c.subcategories.length} subcategories
                    </span>
                  </button>
                ))}
              </div>
            </section>
          </div>
        )}
      </main>
    </div>
  );
}
